import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import {
  TranscriptionResult,
  AIAnalysisRequest,
  AIAnalysisResponse,
  InterviewChatRequest,
  InterviewChatResponse,
  GenerateQuestionsRequest,
  GenerateQuestionsResponse,
  EvaluateAnswerRequest,
  EvaluateAnswerResponse,
} from '../types';
import { logApiError, logSlowRequest } from './errorLogger';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5555';

// Slow request threshold in milliseconds (8 seconds)
const SLOW_REQUEST_THRESHOLD_MS = 8000;

// Store for slow request timeouts (keyed by request ID)
const slowRequestTimeouts = new Map<string, NodeJS.Timeout>();

// Generate unique request ID
const generateRequestId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Extended config type with metadata
interface ConfigWithMetadata extends InternalAxiosRequestConfig {
  metadata?: {
    startTime: number;
    requestId: string;
  };
}

// Create axios instance with interceptors
const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

// Request interceptor - add timestamp and slow request tracking
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const requestId = generateRequestId();
    const configWithMeta = config as ConfigWithMetadata;

    // Add request metadata
    configWithMeta.metadata = {
      startTime: Date.now(),
      requestId,
    };

    // Set up slow request warning timer
    const endpoint = config.url || 'unknown';
    const method = config.method?.toUpperCase() || 'UNKNOWN';

    const timeoutId = setTimeout(() => {
      logSlowRequest(endpoint, method, SLOW_REQUEST_THRESHOLD_MS / 1000);
      slowRequestTimeouts.delete(requestId);
    }, SLOW_REQUEST_THRESHOLD_MS);

    slowRequestTimeouts.set(requestId, timeoutId);

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Helper to clear slow request timeout
const clearSlowRequestTimeout = (config: ConfigWithMetadata | undefined): void => {
  if (config?.metadata?.requestId) {
    const timeoutId = slowRequestTimeouts.get(config.metadata.requestId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      slowRequestTimeouts.delete(config.metadata.requestId);
    }
  }
};

// Response interceptor - log errors and clear slow request timer
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Clear slow request timeout on success
    clearSlowRequestTimeout(response.config as ConfigWithMetadata);
    return response;
  },
  (error: AxiosError) => {
    // Clear slow request timeout on error
    clearSlowRequestTimeout(error.config as ConfigWithMetadata);

    const endpoint = error.config?.url || 'unknown';
    const statusCode = error.response?.status;
    const requestData = error.config?.data;

    // Log the API error
    logApiError(endpoint, error, statusCode, requestData);

    return Promise.reject(error);
  }
);

export const transcribeAudio = async (audioBlob: Blob, expectedText?: string): Promise<TranscriptionResult> => {
  const formData = new FormData();
  formData.append('audio', audioBlob, 'recording.webm');

  // Send expected text to enable word_boost for better recognition
  if (expectedText) {
    formData.append('expectedText', expectedText);
  }

  const response = await apiClient.post<TranscriptionResult>(`/api/transcribe`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });

  return response.data;
};

// Pro Analysis types (Azure Speech Pronunciation Assessment)
export interface PhonemeAnalysis {
  phoneme: string;
  accuracyScore: number;
  nBestPhonemes?: Array<{
    phoneme: string;
    score: number;
  }>;
}

export interface SyllableAnalysis {
  syllable: string;
  accuracyScore: number;
}

export interface WordAnalysis {
  target: string;
  spoken: string;
  status: 'correct' | 'incorrect' | 'partial' | 'missed' | 'extra';
  accuracyScore: number;
  errorType: 'None' | 'Omission' | 'Insertion' | 'Mispronunciation';
  phonemes?: PhonemeAnalysis[];
  syllables?: SyllableAnalysis[];
}

export interface ProAnalysis {
  transcribedText: string;
  accuracy: number;
  fluency: number;
  completeness: number;
  pronunciationScore: number;
  prosodyScore?: number;
  overallScore: number;
  totalDuration: number;
  wordAnalysis: WordAnalysis[];
  tips: string[];
}

export interface ProTranscriptionResult extends TranscriptionResult {
  proAnalysis: ProAnalysis;
}

// Pro transcription using OpenAI Whisper
export const transcribeAudioPro = async (audioBlob: Blob, targetText: string): Promise<ProTranscriptionResult> => {
  const formData = new FormData();
  formData.append('audio', audioBlob, 'recording.webm');
  formData.append('targetText', targetText);

  const response = await apiClient.post<ProTranscriptionResult>(`/api/transcribe/pro`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });

  return response.data;
};

export const analyzeResults = async (data: AIAnalysisRequest): Promise<AIAnalysisResponse> => {
  const response = await apiClient.post<AIAnalysisResponse>(`/api/analyze`, data);
  return response.data;
};

// Interview Practice API functions

export const interviewChat = async (data: InterviewChatRequest): Promise<InterviewChatResponse> => {
  const response = await apiClient.post<InterviewChatResponse>(`/api/interview/chat`, data);
  return response.data;
};

export const generateInterviewQuestions = async (data: GenerateQuestionsRequest): Promise<GenerateQuestionsResponse> => {
  const response = await apiClient.post<GenerateQuestionsResponse>(`/api/interview/generate-questions`, data);
  return response.data;
};

export const evaluateAnswer = async (data: EvaluateAnswerRequest): Promise<EvaluateAnswerResponse> => {
  const response = await apiClient.post<EvaluateAnswerResponse>(`/api/interview/evaluate-answer`, data);
  return response.data;
};

// Follow-up question generation
export interface GenerateFollowUpRequest {
  jobTitle: string;
  previousQuestion: string;
  previousAnswer: string;
  questionNumber: number;
  totalQuestions: number;
  conversationHistory: Array<{ question: string; answer: string }>;
  hellMode?: boolean;
}

export interface GenerateFollowUpResponse {
  question: string;
  isFollowUp: boolean;
}

export const generateFollowUpQuestion = async (data: GenerateFollowUpRequest): Promise<GenerateFollowUpResponse> => {
  const response = await apiClient.post<GenerateFollowUpResponse>(`/api/interview/generate-followup`, data);
  return response.data;
};

// Phonemes API functions

export interface PhonemeBreakdown {
  phoneme: string;
  ipa: string;
  difficulty: 'easy' | 'medium' | 'hard';
  tip: string;
  stressed?: boolean;
}

export interface WordPhonemes {
  word: string;
  phonemes: PhonemeBreakdown[];
  ipa: string;
  found: boolean;
}

export interface MultipleWordPhonemesResponse {
  words: WordPhonemes[];
}

export const getWordPhonemes = async (word: string): Promise<WordPhonemes> => {
  const response = await apiClient.get<WordPhonemes>(`/api/phonemes/word/${encodeURIComponent(word)}`);
  return response.data;
};

export const getMultipleWordPhonemes = async (words: string[]): Promise<WordPhonemes[]> => {
  const response = await apiClient.post<MultipleWordPhonemesResponse>(`/api/phonemes/words`, { words });
  return response.data.words;
};

// Text Rewrite API

export type RewriteStyle = 'business' | 'original' | 'informal';

export interface RewriteTextResponse {
  success: boolean;
  rewrittenText: string;
  style: RewriteStyle;
}

export const rewriteText = async (text: string, style: RewriteStyle): Promise<RewriteTextResponse> => {
  const response = await apiClient.post<RewriteTextResponse>(`/api/rewrite`, { text, style });
  return response.data;
};

// Idea of the Day - Get inspiring practice text
export interface IdeaOfTheDayResponse {
  success: boolean;
  text: string;
  theme: string;
}

export const getIdeaOfTheDay = async (): Promise<IdeaOfTheDayResponse> => {
  const response = await apiClient.get<IdeaOfTheDayResponse>(`/api/rewrite/idea-of-the-day`);
  return response.data;
};

// Text-to-Speech API
export type TTSVoice =
  | 'en-GB-SoniaNeural'
  | 'en-GB-RyanNeural'
  | 'en-US-JennyNeural'
  | 'en-US-GuyNeural';

export interface TTSVoiceOption {
  id: TTSVoice;
  name: string;
  accent: string;
  gender: 'female' | 'male';
}

export interface TTSVoicesResponse {
  voices: TTSVoiceOption[];
}

export const getTTSVoices = async (): Promise<TTSVoicesResponse> => {
  const response = await apiClient.get<TTSVoicesResponse>(`/api/speech/voices`);
  return response.data;
};

export const synthesizeSpeech = async (text: string, voice?: TTSVoice): Promise<Blob> => {
  const response = await apiClient.post(`/api/speech/synthesize`, { text, voice }, {
    responseType: 'blob',
  });
  return response.data;
};

// ========== Practice Sounds / Tutor API ==========

export interface SoundCategory {
  id: string;
  name: string;
  sounds: string[];
  description: string;
  examples: string[];
  textCount: number;
}

export interface PracticeText {
  text: string;
  level: 'A1' | 'A2' | 'B1' | 'B2' | 'C1';
  topic: string;
  targetSounds: string[];
  wordCount: number;
}

export interface ProblemSound {
  category: string;
  sound: string;
  frequency: number;
  lastOccurrence: Date;
  examples: string[];
}

export interface RoadmapStep {
  id: string;
  category: string;
  title: string;
  description: string;
  targetSounds: string[];
  exercises: string[];
  status: 'not_started' | 'in_progress' | 'completed';
  progress: number;
  estimatedSessions: number;
}

export interface UserPronunciationProfile {
  userId: string;
  problemSounds: ProblemSound[];
  totalSessions: number;
  averageScore: number;
  currentFocusArea: string | null;
  roadmap: RoadmapStep[];
  isNew?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export const getSoundCategories = async (): Promise<SoundCategory[]> => {
  const response = await apiClient.get<SoundCategory[]>('/api/tutor/categories');
  return response.data;
};

export const getPracticeTexts = async (
  categories?: string[],
  count?: number
): Promise<{ texts: PracticeText[]; totalAvailable: number }> => {
  const params = new URLSearchParams();
  if (categories?.length) params.append('categories', categories.join(','));
  if (count) params.append('count', count.toString());
  const response = await apiClient.get(`/api/tutor/texts?${params}`);
  return response.data;
};

export const getRandomPracticeText = async (category?: string): Promise<PracticeText> => {
  const url = category
    ? `/api/tutor/texts/random?category=${category}`
    : '/api/tutor/texts/random';
  const response = await apiClient.get<PracticeText>(url);
  return response.data;
};

export const getUserPronunciationProfile = async (userId: string): Promise<UserPronunciationProfile> => {
  const response = await apiClient.get<UserPronunciationProfile>(`/api/tutor/profile/${userId}`);
  return response.data;
};

export const updatePronunciationSession = async (
  userId: string,
  score: number,
  phonemeAnalysis?: { phoneme: string; accuracy: number; word: string }[]
): Promise<UserPronunciationProfile> => {
  const response = await apiClient.post<UserPronunciationProfile>(
    `/api/tutor/profile/${userId}/session`,
    { score, phonemeAnalysis }
  );
  return response.data;
};

export const getUserRoadmap = async (userId: string): Promise<{
  roadmap: RoadmapStep[];
  problemSounds: ProblemSound[];
  averageScore: number;
  totalSessions: number;
}> => {
  const response = await apiClient.get(`/api/tutor/roadmap/${userId}`);
  return response.data;
};
